import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Button, Dialog } from "@mui/material";
import axios from "axios";
import baseURL from "../../../configs/api";
import { saveAs } from "file-saver";
import { useTheme } from "../../../components/hooks/useTheme";
import colorConfigs from "../../../configs/colorConfigs";
import useAuth from "../../../components/hooks/useAuth";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";
import moment from "moment";
import useGeneral from "../../../components/hooks/useGeneral";

type Props = {
  handlePopup: () => void;
  showPopup: boolean;
  setPopup: (value: boolean) => void;
  fileName: FileList | null;
  setFile: (value: FileList | null) => void;
  fileSize: number;
  setFileSize: (value: number) => void;
  uploadedAt: string;
  uploadedBy: string;
  setFileName: (name: string) => void;
  filePath: string;
  saved: boolean;
  item: any;
};

export default function ImagePopup({
  showPopup,
  setPopup,
  handlePopup,
  fileName,
  setFile,
  fileSize,
  setFileSize,
  uploadedAt,
  uploadedBy,
  setFileName,
  filePath,
  saved,
  item
}: Props) {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { auth }: any = useAuth();
  const { handleTenantError }: any = useTenant();
  const { dateFormat }: any = useGeneral();
  const { themeStyles } = useTheme();
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [isSaved, setSaved] = useState<boolean>(saved);
  const [newFileName, setNewFileName] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const inputFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showPopup) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => document.body.classList.remove("overflow-hidden");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopup]);

  const saveTheFile = () => {
    if (fileName) {
      if (!isProcessing) {
        setIsProcessing(true);
        const formData = new FormData();
        formData.append("ImageFile", fileName[0]);

        axios
          .post(`${baseURL}customerFile/upload`, formData, {
            headers: {
              'X-Tenant-ID': tenantId,
              "Content-Type": "multipart/form-data"
            },
            withCredentials: true,
          })
          .then((response) => {
            setSaved(true);
            setFileName(response.data);
            setNewFileName(response.data);
            if (item.type.split(" ")[0] === "PUT") {
              axios
                .put(
                  `${baseURL}customerExtraInformation?userId=${auth?.id}`,
                  JSON.stringify({
                    id: item.type.split(" ")[1],
                    idNumber: item.idNumber,
                    issueDate: new Date(item.issueDate),
                    exprationDate: new Date(item.exprationDate),
                    filePath: response.data,
                    idTypeId: item.idTypeId,
                    customerId: item.customerId,
                    imageSize: fileSize,
                    uploadedAt: new Date(item.uploadedAt),
                    uploadedBy: item.uploadedBy
                  }),
                  {
                    headers: {
                      'X-Tenant-ID': tenantId,
                      "Content-Type": "application/json"
                    },
                    withCredentials: true,
                  }
                )
                .then(() => {
                  setIsProcessing(false);
                  handlePopup();
                  handleTenantError(null);
                })
                .catch((error) => {
                  setIsProcessing(false);
                  handleTenantError(error);
                })
            }
            handleTenantError(null);
          })
          .catch((error) => {
            handleTenantError(error);
          });
      }
    }
  };

  const downloadTheImage = () => {
    saveAs(
      `${baseURL}customerFile/download?name=${!saved ? newFileName : filePath}`,
      `${!saved ? newFileName : filePath}.png`
    );
  };


  const onFileChangeCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    /*Selected files data can be collected here.*/
    const selectedFiles = e.target.files as FileList;
    if (selectedFiles.length !== 0) {
      setFile(selectedFiles);
      setFileSize(selectedFiles[0].size);
      setSaved(false);
    }
  };

  return (
    <>
      <Dialog
        open={showPopup}
        maxWidth="md"
        fullScreen={isFullScreen}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: isFullScreen ? "100%" : "45%",
            width: '100%',
            borderRadius: isFullScreen ? "0px" : '16px',
            backgroundColor: themeStyles.sideBar.bg,
          },
        }}
      >
        <div className={`px-[10px] py-[5px] h-full ${themeStyles.main.tailwindSectionBg}`}>
          <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
            <div className="w-full mt-2">
              <div className={`w-full`}>
                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>

                  <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Preview</h2>

                  <CloseIcon
                    className="cursor-pointer"
                    sx={{
                      color: themeStyles.main.iconColor,
                      fontSize: "20px",
                    }}
                    onClick={() => {
                      setIsFullScreen(false);
                      handlePopup();
                    }}
                  />
                </div>

                <div className="mt-2 p-2">
                  <div className="w-full">
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div>
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Filename</span>
                          <br />
                          <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{!saved ? fileName && fileName[0]?.name : filePath}</span>
                        </div>

                        <div>
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Uploaded At</span>
                          <br />
                          <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{uploadedAt !== null
                            ? moment(new Date(uploadedAt)).format(dateFormat + " hh:mm a")
                            : ""}</span>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Size</span>
                          <br />
                          <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{fileSize === 0 ? 0 : (fileSize / 1024)?.toFixed(2)} KB</span>
                        </div>

                        <div>
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Uploaded By</span>
                          <br />
                          <span className={`text-xs font-medium ${themeStyles.main.textSubHeadingColor}`}>{uploadedBy !== null ? uploadedBy : "N/A"}</span>
                        </div>
                      </div>
                    </div>

                    <div className="w-full mt-4">
                      {
                        fileName
                          ? (
                            <img
                              src={
                                !isSaved
                                  ? fileName && URL.createObjectURL(fileName[0])
                                  : `${baseURL}customerFile/Get?name=${filePath}`
                              }
                              style={{
                                width: "100%",
                                height: isFullScreen ? "calc(100% - 175px)" : "300px",
                              }}
                              alt="info"
                            />
                          ) : (
                            <img
                              src={!isSaved ? "" : `${baseURL}customerFile/Get?name=${filePath}`}
                              style={{
                                width: "100%",
                                height: isFullScreen ? "calc(100% - 175px)" : "300px",
                              }}
                              alt="info"
                            />
                          )
                      }
                    </div>

                    <div className="w-full flex mt-4 gap-2.5">
                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.main.bg
                        }}
                        onClick={() => setIsFullScreen(!isFullScreen)}
                      >
                        {isFullScreen && "Exit "} Full Screen
                      </Button>

                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.main.bg
                        }}
                        onClick={() => {
                          if (inputFileRef.current) {
                            inputFileRef.current.value = ""
                          }
                          inputFileRef.current?.click()
                        }}
                      >
                        Update New File
                      </Button>

                      {
                        isSaved
                          ? (
                            <Button variant="contained"
                              sx={{
                                backgroundColor: colorConfigs.btn.main.bg
                              }}
                              onClick={() => downloadTheImage()}
                            >
                              Download
                            </Button>
                          ) : (
                            <Button variant="contained"
                              sx={{
                                backgroundColor: colorConfigs.btn.main.bg
                              }}
                              onClick={() => saveTheFile()}
                            >
                              {isProcessing ? "Processing..." : "Save"}
                            </Button>
                          )
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </Dialog>

      <input
        type="file"
        accept="image/*"
        ref={inputFileRef}
        className="hidden"
        onChange={onFileChangeCapture}
      />
    </>

  );
}
